/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
        import { EzChar } from '/ezlibrary/helpers/EzChar.js';
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for Booleans
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import {
 *         // other healper classes ...,
 *         EzBoolean
 *     } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static class reference: EzBoolean
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzBoolean extends EzStaticClass {
    /**
     * @public @readonly @property
     * Gets the integer value representing false: 0
     * @returns {number}
     */
    static get intFalse() {
        return 0;
    }

    /**
     * @public @readonly @property
     * Gets the integer value representing true: 1
     * @returns {number}
     */
    static get intTrue() {
        return 1;
    }

    /**
     * @public @readonly @property
     * Gets the integer value representing false: '0'
     * @returns {number}
     */
    static get intStrFalse() {
        return '0';
    }

    /**
     * @public @readonly @property
     * Gets the integer value representing true: '1'
     * @returns {number}
     */
    static get intStrTrue() {
        return '1';
    }

    /**
     * @public @readonly @property
     * Gets the integer value representing false: 'false'
     * @returns {number}
     */
    static get strFalse() {
        return 'false';
    }

    /**
     * @public @readonly @property
     * Gets the string value representing true: 'true'
     * @returns {number}
     */
    static get strTrue() {
        return 'true';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets boolean type string from typeof true
     * Boolean type string: 'boolean'
     * @returns {string}
     */
    static get BOOLEAN_TYPE() {
        return typeof true;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the object type string of the boolean type.
     * Object type string: '[object Boolean]'
     * @returns {string}
     */
    static get BOOLEAN_OBJECT_TYPE() {
        return Object.prototype.toString.call(true);
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If the provided booleanValue = true, then transforms to the number 1'
     * 2) If the provided booleanValue = false, then transforms to the number 0
     * 3) If the provided booleanValue is not a boolean, then transforms to the number 0
     * @param {booleanValue} booleanValue
     * Default: false
     * @returns {number}
     */
    static toNumber(booleanValue = false) {
        return EzBoolean.toNumberOrDefault(
            booleanValue,
            EzBoolean.intFalse);
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If the provided booleanValue = true, then transforms to the number 1'
     * 2) If the provided booleanValue = false, then transforms to the number 0
     * 3) If the provided booleanValue is not a boolean, then returns the defaultValue
     * @param {booleanValue} booleanValue
     * Default: false
     * @param {undefined|null|boolean|number|string|*} defaultValue
     * Default: 0
     * @returns {number}
     */
    static toNumberOrDefault(booleanValue = false, defaultValue = EzBoolean.intFalse) {
        if (EzBoolean.isBoolean(booleanValue)) {
            return booleanValue
                // true
                ? EzBoolean.intTrue
                // false
                : EzBoolean.intFalse;
        }

        return defaultValue;
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If the provided booleanValue = true, then transforms to the string '1'
     * 2) If the provided booleanValue = false, then transforms to the string '0'
     * 3) If the provided booleanValue is not a boolean, then transforms to the string '0'
     * @param {booleanValue} booleanValue
     *
     * Default: false
     *
     * @returns {string}
     * Returns a string representation of the provided booleanValue
     */
    static toNumberString(booleanValue = false) {
        return EzBoolean.toNumber(booleanValue).toString();
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If the provided booleanValue = true, then transforms to the string '1'
     * 2) If the provided booleanValue = false, then transforms to the string '0'
     * 3) If the provided booleanValue is not a boolean, then returns the defaultValue.
     *
     * @param {booleanValue} booleanValue
     * Default: false
     * @param {undefined|null|number|*} defaultValue
     * Default: 0 (aka false)
     *
     * @returns {string|null}
     * Returns a number representing the provided booleanValue or the provided defaultValue
     */
    static toNumberStringOrDefault(booleanValue = false, defaultValue = EzBoolean.intFalse) {
        const result = EzBoolean.isBoolean(booleanValue)
            ? EzBoolean.toNumberOrDefault(
                booleanValue,
                defaultValue)
            : defaultValue;

        return undefined !== result && null != result
            ? result.toString()
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) Transforms to 'true' if booleanValue = true and stringNumber = false
     * 2) Transforms to 'false' if booleanValue = false, undefined, or null and stringNumber = false
     * 3) Transforms to '1' if booleanValue = true and stringNumber = true
     * 4) Transforms to '0' if booleanValue = false, undefined, or null and stringNumber = true
     *
     * @param {booleanValue} booleanValue
     * Default: false
     * @param {boolean} stringNumber
     * Default: false
     *
     * @returns {string}
     * Returns a string value representation of the provided booleanValue
     */
    static asString(booleanValue = false, stringNumber = false) {
        return EzBoolean.asStringOrDefault(
            booleanValue,
            false,
            stringNumber);
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If booleanValue is not a boolean, then returns the provided defaultBooleanValue
     * 2) Transforms to 'true' if booleanValue = true and stringNumber = false
     * 3) Transforms to 'false' if booleanValue = false, undefined, or null and stringNumber = false
     * 4) Transforms to '1' if booleanValue = true and stringNumber = true
     * 5) Transforms to '0' if booleanValue = false, undefined, or null and stringNumber = true
     *
     * @param {undefined|null|boolean|*} booleanValue
     * Default: null
     * @param {undefined|null|boolean|*} defaultBooleanValue
     *
     * @returns {boolean}
     * Returns a string value representation of the provided booleanValue or the provided defaultBooleanValue
     */
    static asStringOrDefault(booleanValue = null, defaultBooleanValue = false, stringNumber = false) {
        if (!EzBoolean.isBoolean(booleanValue)) {
            return defaultBooleanValue;
        }

        return EzBoolean.booleanOrFalse(stringNumber)
            ? EzBoolean.toNumberString(booleanValue)
            : booleanValue.toString();
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) Transforms to 'TRUE' if booleanValue = true and stringNumber = false
     * 2) Transforms to 'FALSE' if booleanValue = false, undefined, or null and stringNumber = false
     * 3) Transforms to '1' if booleanValue = true and stringNumber = true
     * 4) Transforms to '0' if booleanValue = false, undefined, or null and stringNumber = true
     * @param {booleanValue} booleanValue
     * Default: false
     *
     * @returns {string}
     * Returns the upper case string representation of the provided booleanValue
     */
    static asUpperString(booleanValue, stringNumber = false) {
        return EzBoolean.asString(booleanValue, stringNumber)
            .toUpperCase();
    }

    /**
     * @static
     * @public @method
     * Transform the provided booleanValue to one of the following:
     * 1) If booleanValue is not a boolean, then returns the provided defaultBooleanValue
     * 2) Transforms to 'TRUE' if booleanValue = true and stringNumber = false
     * 3) Transforms to 'FALSE' if booleanValue = false, undefined, or null and stringNumber = false
     * 4) Transforms to '1' if booleanValue = true and stringNumber = true
     * 5) Transforms to '0' if booleanValue = false, undefined, or null and stringNumber = true
     *
     * @param {undefined|null|boolean|*} booleanValue
     * Default: null
     * @param {undefined|null|boolean|*} defaultBooleanValue
     *
     * @returns {string|*}
     * Returns the upper case string representation of the provided booleanValue or the provided defaultBooleanValue
     */
    static asUpperStringOrDefault(booleanValue = null, defaultBooleanValue = false, stringNumber = false) {
        const result = EzBoolean.asStringOrDefault(
            booleanValue,
            defaultBooleanValue,
            stringNumber);

        return 'string' === typeof result
            ? result.toUpperCase()
            : result;
    }


    /**
     * @static
     * @public @method
     * Transforms the provided value in one of the following ways:
     * 1) If value is undefined or null, then false is returned.
     * 2) If the value is a boolean, then the value is returend as provided
     * 3) If the value is a number from 1 to positive infinity, then true is returned.
     * 4) If the value is a number fomr 0 down to negative infinity then false is returned.
     * 5) If the value is a string and the upper case value is equal to '0', 'NO', 'FALSE', 'F', 'N', 'CANCEL'
     *    'C', 'UNCHECKED', '-', or 'DENIED', then false is returned.
     * 6) If the value is a string and the upper case value is equal to '1', 'YES', 'TRUE', 'T', 'Y', 'O',
     *    'CHECKED', 'X', '+', or 'ACCEPTED', then true is returned.
     * 7) Otherwise, false is returned
     *
     * @param {undefined|null|string|number|boolean} value
     * @param {undefined|null|string|number|boolean|*} defaultValue
     * Default: false
     *
     * @returns {boolean}
     */
    static toBoolean(value) {
        return EzBoolean.toBooleanOrDefault(value, false);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value in one of the following ways:
     * 1) If value is undefined or null, then the defaultValue is returned
     * 2) If the value is a boolean, then the value is returend as provided
     * 3) If the value is a number from 1 to positive infinity, then true is returned.
     * 4) If the value is a number fomr 0 down to negative infinity then false is returned.
     * 5) If the value is a string and the upper case value is equal to '0', 'NO', 'FALSE', 'F', 'N', 'CANCEL'
     *    'C', 'UNCHECKED', '-', or 'DENIED', then false is returned.
     * 6) If the value is a string and the upper case value is equal to '1', 'YES', 'TRUE', 'T', 'Y', 'O',
     *    'CHECKED', 'X', '+', or 'ACCEPTED', then true is returned.
     * 7) Otherwise, the defaultValue is returned.
     *
     * @param {undefined|null|string|number|boolean} value
     * @param {undefined|null|string|number|boolean|*} defaultValue
     * Default: false
     *
     * @returns {*|boolean}
     * Note: The defaultValue can be of any type
     */
    static toBooleanOrDefault(value = false, defaultValue = false) {
        if (EzBoolean.isBoolean(value)) {
            return value;
        }

        if (undefined === value || null == value) {
            return defaultValue;
        }

        if ('number' === typeof value) {
            // Values 1 to positive infinity evaluate to true
            // Values 0 to negative infinity evaluate to false
            return 1 <= value;
        }

        if ('string' === typeof value) {
            if (0 === value.length) {
                // A no-length value is evaluated as false
                return false;
            }

            switch (value.toUpperCase()) {
                case '0':
                case 'NO':
                case 'FALSE':
                case 'F':
                case 'N':
                case 'CANCEL':
                case 'C':
                case 'UNCHECKED':
                case '-':
                case 'DENIED':
                    return false;
                case '1':
                case 'YES':
                case 'TRUE':
                case 'T':
                case 'Y':
                case 'O':
                case 'CHECKED':
                case 'X':
                case '+':
                case 'ACCEPTED':
                    return true;
                default:
                    return defaultValue;
            }
        }

        return defaultValue;
    }

    /**
     * @static
     * @public @method
     * Determines if the passed object is a boolean type.
     * @param {*} aObject
     * @returns {boolean}
     */
    static isBoolean(aObject) {
        return undefined !== aObject && null !== aObject &&
            (EzBoolean.BOOLEAN_TYPE === typeof aObject || EzBoolean.BOOLEAN_OBJECT_TYPE === EzObject.getObjectType(aObject));
    }

    /**
     * @static
     * @public @method
     * Returns true if the boolean value is true. Otherwise, returns false if the value is NOT a boolean at all OR
     * is actually false.
     * @param {boolean} aValue
     * @returns {boolean}
     */
    static isTrue(aValue) {
        return undefined !== aValue && null !== aValue &&
            (EzBoolean.BOOLEAN_TYPE === typeof aValue || EzBoolean.BOOLEAN_OBJECT_TYPE === EzObject.getObjectType(aValue)) &&
            true === aValue;
    }

    /**
     * @static
     * @public @method
     * Returns true if the boolean value is false. Otherwise, returns false if the value is NOT a boolean at all OR
     * is actually true.
     * @param {boolean} aValue
     * @returns {boolean}
     */
    static isFalse(aValue) {
        return undefined !== aValue && null !== aValue &&
            (EzBoolean.BOOLEAN_TYPE === typeof aValue || EzBoolean.BOOLEAN_OBJECT_TYPE === EzObject.getObjectType(aValue)) &&
            false === aValue;
    }

    /**
     * @static
     * @public @method
     * Returns true if all of the provided boolean arguments are true;
     * @param {*} arguments
     * @returns {boolean}
     */
    static allTrue(...args) {
        if (!EzObject.isValid(args)) {
            return false;
        }

        for (const argument of args) {
            if (EzBoolean.isBoolean(argument) && EzBoolean.isFalse(argument)) {
                return false;
            }
        }

        return true;
    }

    /**
     * @static
     * @public @method
     * Returns true if all of the provided boolean arguments are false;
     * @param {*} ...args
     * @returns {boolean}
     */
    static allFalse(...args) {
        for (const argument of args) {
            if (EzBoolean.isBoolean(argument) && EzBoolean.isTrue(argument)) {
                return false;
            }
        }

        return true;
    }

    /**
     * @static
     * @public @method
     * Returns true if ANY of the provided boolean arguments is true
     * @param {*} ...args
     * @returns {boolean}
     */
    static anyTrue(...args) {
        for (const argument of args) {
            if (EzBoolean.isBoolean(argument) && EzBoolean.isTrue(argument)) {
                return true;
            }
        }

        return false;
    }

    /**
     * @static
     * @public @method
     * Returns true if ANY of the provided boolean arguments is false
     * @param {*} ...args
     * @returns {boolean}
     */
    static anyFalse(...args) {
        for (const argument of args) {
            if (EzBoolean.isBoolean(argument) && EzBoolean.isFalse(argument)) {
                return true;
            }
        }

        return false;
    }

    /**
     * @static
     * @public @method
     * If the value is a boolean, the value is returned.
     * Otherwise, false is returned.
     * @param {undefined|null|boolean|*} value
     * @returns {boolean}
     */
    static booleanOrFalse(value) {
        return EzBoolean.isBoolean(value)
            ? value
            : false;
    }

    /**
     * @static
     * @public @method
     * If the value is a boolean, the value is returned.
     * Otherwise, true is returned.
     * @param {undefined|null|boolean|*} value
     * @returns {boolean}
     */
    static booleanOrTrue(value) {
        return EzBoolean.isBoolean(value)
            ? value
            : true;
    }

    /**
     * @static
     * @public @method
     * If the value is a boolean, the value is returned.
     * Otherwise, null is returned.
     * @param {undefined|null|boolean|*} value
     * @returns {boolean}
     */
    static booleanOrNull(value) {
        return EzBoolean.isBoolean(value)
            ? value
            : null;
    }

    /**
     * @static
     * @public @method
     * If the value is a boolean, the value is returned.
     * Otherwise, the provided defaultValue is returned.
     *
     * @param {undefined|null|boolean|*} value
     * @param {undefined|null|boolean|*} defaultValue
     *
     * @returns {boolean}
     */
    static booleanOrDefault(value, defaultValue) {
        return EzBoolean.isBoolean(value)
            ? value
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Evaluates all the provided boolean expressions and if all evaluate to true, then true is returned.
     * Otherwise, false is returned
     * @param {boolean|array} booleanExpressions
     */
    static requireAll(...booleanExpressions) {
        for (const booleanExpression of booleanExpressions) {
            if (!booleanExpression) {
                return false;
            }
        }

        return true;
    }
}
